import { useEffect } from 'react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { MainLayout } from '@/components/layout'
import { GoogleLikeSearch, SearchResults } from '@/components/search'
import { TranslationEditorPage } from '@/components/settings'
import { WordEditor } from '@/components/editor'
import { DictionaryPage } from '@/components/dictionary'
import { GlassCard } from '@/components/ui/GlassCard'
import { useSearchStore } from '@/stores/searchStore'
import { useUIStore } from '@/stores/uiStore'
import { useHealthCheck } from '@/hooks/useSearch'
import { AlertCircleIcon } from '@/components/ui/icons'
import { cn } from '@/utils/cn'

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      refetchOnWindowFocus: false
    }
  }
})

function AppContent() {
  const { error, searchResults, isProgressiveMode } = useSearchStore()
  const { setApiConnected, currentPage } = useUIStore()

  // Check API health on mount
  const healthCheck = useHealthCheck()

  useEffect(() => {
    if (healthCheck.isSuccess) {
      setApiConnected(true)
    } else if (healthCheck.isError) {
      setApiConnected(false)
    }
  }, [healthCheck.isSuccess, healthCheck.isError, setApiConnected])

  // Show translation editor page
  if (currentPage === 'translations') {
    return (
      <MainLayout>
        <TranslationEditorPage />
      </MainLayout>
    )
  }

  // Show word editor page
  if (currentPage === 'editor') {
    return (
      <MainLayout fullWidth>
        <WordEditor />
      </MainLayout>
    )
  }

  // Show dictionary page
  if (currentPage === 'dictionary') {
    return (
      <MainLayout fullWidth>
        <DictionaryPage />
      </MainLayout>
    )
  }

  return (
    <MainLayout>
      <div className="max-w-6xl mx-auto">
        {/* Main Search Interface */}
        <GoogleLikeSearch />

        {/* Error Display */}
        {error && (
          <GlassCard
            variant="default"
            padding="md"
            className={cn(
              'max-w-2xl mx-auto mt-6',
              'bg-red-50 dark:bg-red-900/30',
              'border-2 border-red-200 dark:border-red-800'
            )}
          >
            <div className="flex items-center gap-3 text-red-700 dark:text-red-300">
              <AlertCircleIcon className="w-5 h-5 flex-shrink-0" />
              <div>
                <p className="font-semibold">Error</p>
                <p className="text-sm opacity-90">{error}</p>
              </div>
            </div>
          </GlassCard>
        )}

        {/* Search Results */}
        {(searchResults || isProgressiveMode) && (
          <div className="mt-10">
            <SearchResults />
          </div>
        )}
      </div>
    </MainLayout>
  )
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AppContent />
    </QueryClientProvider>
  )
}
